import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import React, { createContext, useContext } from "react";

const Title = styled(DialogTitle)(
  ({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(
        1,
        2
      )
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between'
  }));
const TitleNav = styled('div')(
  () => ({
    display: 'flex',
    alignItems: 'center'
  }));
const CloseButton = styled(IconButton)(
  () => ({
    zIndex: 999
  }));
const Content = styled(DialogContent)(
  ({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1)
    }
  }));
const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));
const Image = styled('img')(() => ({
  display: 'block',
  width: '100%',
  height: 'auto',
  maxHeight: 'calc(100vh - 180px)',
  objectFit: 'contain',
}));
const ButtonContainer = styled(Box)(() => ({
  position: 'sticky',
  bottom: 0,
  justifyContent: 'center',
  py: 2,
}));

type ImagePreviewDialogType = {
  openImagePreviewDialog: (
    image: { name: string, src: string },
    onDownload?: () => void,
    onclose?: () => void
  ) => void;
}

export const ImagePreviewDialogContext = createContext<ImagePreviewDialogType>(undefined!);

export default function ImagePreviewDialogProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [image, setImage] = React.useState<{ name: string, src: string } | null>(null);
  const [hasDownloadCallback, setHasDownloadCallback] = React.useState(false);

  const downloadCallback = React.useRef<() => void>(undefined);
  const closeCallback = React.useRef<() => void>(undefined);

  const openImagePreviewDialog = (
    image: { name: string, src: string },
    onDownload?: () => void,
    onclose?: () => void
  ) => {
    setImage(image);
    downloadCallback.current = onDownload;
    closeCallback.current = onclose;
    setHasDownloadCallback(!!onDownload);
    setDialogOpen(true);
  }

  const handleDialogClose = () => {
    setDialogOpen(false);
    setImage(null);
    setHasDownloadCallback(false);
    downloadCallback.current = undefined;
    if (closeCallback.current) {
      closeCallback.current();
      closeCallback.current = undefined;
    }
  };

  const handleDownloadClicked = () => {
    if (downloadCallback.current) {
      downloadCallback.current();
    }
  };

  const value = React.useMemo(() => ({ openImagePreviewDialog }), []);

  return (
    <ImagePreviewDialogContext.Provider value={value}>
      {children}
      <Dialog
        open={dialogOpen}
        maxWidth='lg'
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(3px) saturate(200%)',
              overflow: 'visible',
              maxHeight: 'none',
              m: 2,
            }
          }
        }}
      >
        <Title>
          <TitleNav>
            <Typography variant="h5">
              {image?.name}
            </Typography>
          </TitleNav>
          <span>
            <Tooltip title="Close" placement="bottom">
              <span>
                <CloseButton
                  aria-label="close"
                  onClick={handleDialogClose}
                  size="medium"
                  edge="end">
                  <CloseIcon fontSize="large" />
                </CloseButton>
              </span>
            </Tooltip>
          </span>
        </Title>
        <Content sx={{
          display: "flex",
          flexDirection: 'column',
          alignItems: 'center',
          p: 0,
          overflow: 'hidden',
        }}>
          <Container>
            <Image
              src={image?.src}
              alt={image?.name}
            />
            {hasDownloadCallback && (
              <ButtonContainer>
                <Button
                  variant="text"
                  size="large"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadClicked}>
                  Download
                </Button>
              </ButtonContainer>
            )}
          </Container>
        </Content>
      </Dialog>
    </ImagePreviewDialogContext.Provider>
  );
}

export function useImagePreviewDialog() {
  const context = useContext(ImagePreviewDialogContext);
  if (context === undefined) {
    throw new Error("useImagePreviewDialog must be used within a ImagePreviewDialogProvider");
  }
  return context;
}
